import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ExternalLink, 
  Briefcase, 
  Building2, 
  MapPin, 
  Clock, 
  Phone, 
  Mail,
  CheckCircle,
  User,
  Target,
  Loader2,
  Calendar
} from 'lucide-react';
import { RegisterModal } from '../components/RegisterModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_LINK = "https://cal.com/hireforskillz/15min";

export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);

  // Convert Google Drive sharing links to direct image URLs
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const getDirectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=400&h=500&fit=crop';
    
    // Handle relative API paths (persistent across deployments)
    if (url.startsWith('/api/')) {
      return `${BACKEND_URL}${url}`;
    }
    
    // Handle old full URLs with /api/images/
    if (url.includes('/api/images/')) {
      const imageId = url.split('/api/images/')[1];
      return `${BACKEND_URL}/api/images/${imageId}`;
    }
    
    let fileId = null;
    if (url.includes('drive.google.com/open?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    } else if (url.includes('drive.google.com/file/d/')) {
      fileId = url.split('/d/')[1]?.split('/')[0];
    } else if (url.includes('drive.google.com/uc?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    
    if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
    return url;
  };

  useEffect(() => {
    fetchCandidate();
  }, [id, isAuthenticated, token]);

  const fetchCandidate = async () => {
    try {
      if (isAuthenticated) {
        // Get full details for authenticated users
        const response = await axios.get(`${API}/candidates/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCandidate(response.data);
      } else {
        // Get public details for non-authenticated users via dedicated endpoint
        const response = await axios.get(`${API}/candidates/${id}/public`);
        setCandidate(response.data);
      }
    } catch (error) {
      console.error('Error fetching candidate:', error);
      toast.error('Candidate not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPortfolio = () => {
    if (!isAuthenticated) {
      setRegisterOpen(true);
      return;
    }
    if (candidate?.portfolio_link) {
      window.open(candidate.portfolio_link, '_blank');
    } else {
      toast.error('Portfolio link not available');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-hfs-navy" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Candidate not found</p>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isImmediatelyAvailable = candidate.notice_period?.toLowerCase().includes('immediate') || 
    candidate.notice_period?.toLowerCase().includes('0');

  return (
    <div className="min-h-screen bg-slate-50">
      <RegisterModal open={registerOpen} onOpenChange={setRegisterOpen} />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/#candidates" className="flex items-center gap-2 text-slate-600 hover:text-hfs-navy transition-colors">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Candidates</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Photo & Quick Actions */}
          <div className="md:col-span-1">
            <Card className="overflow-hidden">
              {/* Photo */}
              <div className="aspect-[3/4] overflow-hidden bg-slate-100">
                <img 
                  src={getDirectImageUrl(candidate.photo_url)} 
                  alt={candidate.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=400&h=500&fit=crop';
                  }}
                />
              </div>
              
              {/* Badges */}
              <div className="p-4 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  {candidate.is_placed && (
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Hired
                    </Badge>
                  )}
                  {isImmediatelyAvailable && !candidate.is_placed && (
                    <Badge className="bg-blue-500 text-white">
                      <Clock className="h-3 w-3 mr-1" />
                      Immediately Available
                    </Badge>
                  )}
                  {candidate.willing_to_relocate?.toLowerCase().includes('yes') && (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      Open to Relocate
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
            
            {/* View Portfolio Button */}
            <Button
              onClick={handleViewPortfolio}
              className="w-full mt-4 bg-hfs-gold hover:bg-hfs-gold-dark text-hfs-navy h-12 text-base font-semibold"
              data-testid="view-portfolio-btn"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              View Full Portfolio
            </Button>

            {/* Schedule Interview Button */}
            {!candidate.is_placed && (
              <Button
                onClick={() => {
                  if (!isAuthenticated) {
                    setRegisterOpen(true);
                    return;
                  }
                  // Open Cal.com with candidate name as context
                  const calUrl = `${CALENDLY_LINK}?name=${encodeURIComponent(candidate.name)}`;
                  window.open(calUrl, '_blank');
                }}
                className="w-full mt-3 bg-hfs-navy hover:bg-hfs-navy-dark text-white h-12 text-base font-semibold"
                data-testid="schedule-interview-btn"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule an Interview
              </Button>
            )}
          </div>

          {/* Right Column - Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Name & Title */}
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {candidate.name}
              </h1>
              {candidate.strength && (
                <p className="text-lg text-slate-600">{candidate.strength}</p>
              )}
            </div>

            {/* Contact Info - Only for authenticated users */}
            {isAuthenticated ? (
              <Card className="p-5 bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Contact Information
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {candidate.email && (
                    <a href={`mailto:${candidate.email}`} className="flex items-center gap-3 text-slate-700 hover:text-hfs-navy">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <span>{candidate.email}</span>
                    </a>
                  )}
                  {candidate.phone && (
                    <a href={`tel:${candidate.phone}`} className="flex items-center gap-3 text-slate-700 hover:text-hfs-navy">
                      <Phone className="h-5 w-5 text-blue-600" />
                      <span>{candidate.phone}</span>
                    </a>
                  )}
                </div>
              </Card>
            ) : (
              <Card className="p-5 bg-slate-50 border-slate-200 relative overflow-hidden">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Contact Information
                </h3>
                {/* Blurred contact details in background */}
                <div className="relative">
                  <div className="grid sm:grid-cols-2 gap-4 blur-sm select-none pointer-events-none">
                    <div className="flex items-center gap-3 text-slate-700">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <span>candidate.email@company.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <Phone className="h-5 w-5 text-blue-600" />
                      <span>+91 98765 43210</span>
                    </div>
                  </div>
                  {/* Overlay with Click to View button */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
                    <Button 
                      onClick={() => setRegisterOpen(true)}
                      className="bg-hfs-navy hover:bg-hfs-navy-dark text-white shadow-lg"
                      data-testid="click-to-view-contact-btn"
                    >
                      <User className="mr-2 h-4 w-4" />
                      Click to View Contact
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Professional Details */}
            <Card className="p-5">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-hfs-navy" />
                Professional Details
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {candidate.years_of_experience && (
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Briefcase className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Experience</p>
                      <p className="font-medium text-slate-900">{candidate.years_of_experience}</p>
                    </div>
                  </div>
                )}
                {candidate.current_organization && (
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Current Organization</p>
                      <p className="font-medium text-slate-900">{candidate.current_organization}</p>
                    </div>
                  </div>
                )}
                {candidate.expected_ctc && (
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <span className="flex items-center justify-center h-5 w-5 text-slate-600 font-bold text-sm">₹</span>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Expected CTC</p>
                      <p className="font-medium text-slate-900">{candidate.expected_ctc} <span className="text-slate-500 text-sm">(Negotiable)</span></p>
                    </div>
                  </div>
                )}
                {candidate.notice_period && (
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Clock className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Notice Period</p>
                      <p className="font-medium text-slate-900">{candidate.notice_period}</p>
                    </div>
                  </div>
                )}
                {candidate.preferred_work_mode && (
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <MapPin className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Work Mode Preference</p>
                      <p className="font-medium text-slate-900">{candidate.preferred_work_mode}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Looking For */}
            {candidate.looking_for_roles && (
              <Card className="p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-hfs-navy" />
                  Looking For
                </h3>
                <p className="text-slate-700">{candidate.looking_for_roles}</p>
              </Card>
            )}

            {/* Placed Info */}
            {candidate.is_placed && candidate.placed_company_name && (
              <Card className="p-5 bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">Successfully Hired</p>
                    <p className="text-green-700">Now working at {candidate.placed_company_name}</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
